// src/pages/dashboard/DashboardDekan.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  CheckSquare,
  AlertTriangle,
  Clock,
  TrendingUp,
  Building2,
  ChevronRight,
  AlertCircle,
  Hourglass,
  Eye,
} from 'lucide-react';
import { Card, Badge, Breadcrumb, Select, Loading } from '../../components/ui';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { TingkatRisiko } from '../../types';

const RISK_COLORS: Record<string, string> = {
  rendah: '#22C55E',
  sedang: '#F59E0B',
  tinggi: '#F97316',
  kritis: '#EF4444',
  critical: '#EF4444',
};

interface KpiData {
  total_laporan: number;
  menunggu_validasi: number;
  dalam_proses: number;
  selesai: number;
  overdue: number;
  menunggu_konfirmasi: number;
  rata_rata_penyelesaian: number;
}

interface PendingItem {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  risiko?: string;
  unit?: { id: number; name: string };
  user?: { id: number; name: string };
  created_at?: string;
}

interface TindakLanjutItem {
  id: number;
  laporan_masalah_id?: number;
  laporan_masalah?: { id: number; kode: string; judul: string; status?: string };
  laporanMasalah?: { id: number; kode: string; judul: string; status?: string };
  target_date?: string;
  status: string;
  progress_percentage: number;
  pic_user?: { id: number; name: string };
  kondisi_selesai?: string;
}

const DashboardDekan: React.FC = () => {
  const { user } = useAuthStore();
  const fakultas = user?.unit?.name || 'Fakultas';
  const roleName = user?.role?.name?.toLowerCase() || '';
  const isKalem = roleName === 'kalem';
  const roleDisplay = isKalem ? 'Kepala Lembaga' : 'Dekan';

  // States
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState<KpiData>({
    total_laporan: 0,
    menunggu_validasi: 0,
    dalam_proses: 0,
    selesai: 0,
    overdue: 0,
    menunggu_konfirmasi: 0,
    rata_rata_penyelesaian: 0,
  });
  const [pendingValidasi, setPendingValidasi] = useState<PendingItem[]>([]);
  const [tindakLanjut, setTindakLanjut] = useState<TindakLanjutItem[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [risikoData, setRisikoData] = useState<any[]>([]);
  const [periodeList, setPeriodeList] = useState<any[]>([]);
  const [selectedPeriode, setSelectedPeriode] = useState<string>('');
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);

  useEffect(() => {
    const fetchPeriode = async () => {
      try {
        const periodeRes = await api.get('/admin/periode').catch(() => ({ data: { data: [] } }));
        const periodeData = periodeRes.data?.data || periodeRes.data || [];
        const normalizedPeriode = Array.isArray(periodeData) ? periodeData : [];
        setPeriodeList(normalizedPeriode);

        const activePeriode = normalizedPeriode.find((p: any) => p?.is_active);
        setSelectedPeriode(activePeriode ? String(activePeriode.id) : '');
      } catch {
        setPeriodeList([]);
        setSelectedPeriode('');
      } finally {
        setIsPeriodeReady(true);
      }
    };

    fetchPeriode();
  }, []);

  // Fetch data
  useEffect(() => {
    if (!isPeriodeReady) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const params: Record<string, any> = {};
        if (selectedPeriode) params.periode_id = selectedPeriode;

        const [dashboardRes, validasiRes, tindakLanjutRes] = await Promise.all([
          api.get('/dashboard', { params }),
          // ✅ FIX: Tambahkan filter status eksplisit untuk hanya mengambil yang BELUM divalidasi
          api.get('/validasi/level1', {
            params: {
              per_page: 5,
              status: 'diajukan',  // ✅ Hanya ambil yang statusnya 'diajukan'
              ...params
            }
          }),
          api.get('/monitoring/ppepp-progress', { params: { per_page: 10, ...params } }),
        ]);

        // Parse dashboard
        const d = dashboardRes.data?.data || dashboardRes.data;
        if (d) {
          setKpiData({
            total_laporan: d.total_laporan || 0,
            menunggu_validasi: d.laporan_per_status?.diajukan || 0,
            dalam_proses:
              (d.laporan_per_status?.validasi_l1 || 0) +
              (d.laporan_per_status?.validasi_l2 || 0) +
              (d.laporan_per_status?.antrean_rapim || 0) +
              (d.laporan_per_status?.diputuskan || 0) +
              (d.laporan_per_status?.berlangsung || 0),
            selesai:
              (d.laporan_per_status?.selesai || 0) +
              (d.laporan_per_status?.diarsipkan || 0),
            overdue: d.laporan_overdue || 0,
            menunggu_konfirmasi: d.laporan_per_status?.menunggu_konfirmasi || 0,
            rata_rata_penyelesaian: d.rata_rata_penyelesaian || 0,
          });
          setTrendData(d.tren_bulanan || []);

          // Risiko data
          const risiko = d.laporan_per_risiko || {};
          setRisikoData([
            { risiko: 'Rendah', jumlah: risiko.rendah || 0, fill: RISK_COLORS.rendah },
            { risiko: 'Sedang', jumlah: risiko.sedang || 0, fill: RISK_COLORS.sedang },
            { risiko: 'Tinggi', jumlah: risiko.tinggi || 0, fill: RISK_COLORS.tinggi },
            { risiko: 'Kritis', jumlah: risiko.kritis || 0, fill: RISK_COLORS.kritis },
          ]);
        }

        // ✅ FIX: Parse validasi L1 - filter hanya status 'diajukan'
        const validasiData = validasiRes.data?.data || validasiRes.data;
        let validasiList: PendingItem[] = [];

        if (Array.isArray(validasiData)) {
          validasiList = validasiData;
        } else if (validasiData?.data) {
          validasiList = validasiData.data;
        }

        // ✅ FIX: Filter tambahan di frontend untuk memastikan hanya 'diajukan'
        const filteredValidasi = validasiList.filter(
          (lap: PendingItem) => lap.status === 'diajukan'
        );

        setPendingValidasi(filteredValidasi.slice(0, 5));

        // Parse tindak lanjut
        const tlData = tindakLanjutRes.data?.data || tindakLanjutRes.data;
        if (Array.isArray(tlData)) {
          const filteredTl = tlData.filter((item: any) => 
            !['dibatalkan', 'batal'].includes((item.status || '').toLowerCase())
          );
          setTindakLanjut(filteredTl.slice(0, 5));
        } else if (tlData?.data) {
          const filteredTl = tlData.data.filter((item: any) => 
            !['dibatalkan', 'batal'].includes((item.status || '').toLowerCase())
          );
          setTindakLanjut(filteredTl.slice(0, 5));
        }

      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedPeriode, isPeriodeReady]);

  // ========== HELPER FUNCTIONS ==========

  /**
   * ✅ FIX: Cek apakah tindak lanjut sudah selesai atau menunggu konfirmasi
   */
  const isCompletedOrPending = (item: TindakLanjutItem): boolean => {
    const status = (item.status || '').toLowerCase();
    const kondisi = (item.kondisi_selesai || '').toLowerCase();
    const laporanStatus = (
      item.laporan_masalah?.status ||
      item.laporanMasalah?.status ||
      ''
    ).toLowerCase();
    const isLaporanFinal = ['selesai', 'diarsipkan'].includes(laporanStatus);

    // Cek berbagai kondisi selesai
    if (status === 'selesai') return true;
    if (status === 'validasi') return true; // Menunggu konfirmasi
    if (kondisi === 'selesai_final') return true;
    if (kondisi === 'menunggu_konfirmasi') return true;
    if (item.progress_percentage >= 100) return true;
    if (isLaporanFinal) return true;

    return false;
  };

  /**
   * ✅ FIX: Get status label untuk tindak lanjut
   */
  const getTindakLanjutStatusLabel = (item: TindakLanjutItem): { label: string; className: string } => {
    const status = (item.status || '').toLowerCase();
    const kondisi = (item.kondisi_selesai || '').toLowerCase();
    const progress = item.progress_percentage || 0;
    const laporanStatus = (
      item.laporan_masalah?.status ||
      item.laporanMasalah?.status ||
      ''
    ).toLowerCase();
    const isLaporanFinal = ['selesai', 'diarsipkan'].includes(laporanStatus);

    // Selesai final
    if (kondisi === 'selesai_final' || (status === 'selesai' && progress >= 100 && isLaporanFinal)) {
      return { label: '✓ Selesai', className: 'bg-success/10 text-success' };
    }

    // Menunggu konfirmasi
    if (
      kondisi === 'menunggu_konfirmasi'
      || status === 'validasi'
      || (progress >= 100 && status !== 'selesai' && !isLaporanFinal)
    ) {
      return { label: 'Menunggu Konfirmasi', className: 'bg-amber-100 text-amber-700' };
    }

    // Terhambat
    if (status === 'terhambat') {
      return { label: 'Terhambat', className: 'bg-danger/10 text-danger' };
    }

    // Dalam proses
    return { label: `${progress}%`, className: 'bg-warning/10 text-warning' };
  };

  /**
   * Helper: Days remaining - ✅ FIX: Return null jika sudah selesai
   */
  const getDaysRemaining = (item: TindakLanjutItem): number | null => {
    // ✅ FIX: Jika sudah selesai atau menunggu konfirmasi, tidak perlu tampilkan sisa hari
    if (isCompletedOrPending(item)) {
      return null;
    }

    if (!item.target_date) return null;

    return Math.ceil(
      (new Date(item.target_date).getTime() - new Date().setHours(0, 0, 0, 0)) /
      (1000 * 60 * 60 * 24)
    );
  };

  // Loading
  if (loading) {
        return <Loading variant="page" message="Memuat data..." />;
    }

  // Periode options
  const periodeOptions = [
    { value: '', label: 'Semua Periode' },
    ...periodeList.map((p) => ({
      value: String(p.id),
      label: `${p.nama}${p.is_active ? ' (Aktif)' : ''}`,
    })),
  ];

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: roleDisplay }]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">
            Dashboard {roleDisplay}
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <Building2 className="w-4 h-4 inline mr-1" />
            {fakultas}
          </p>
        </div>
        <Select
          options={periodeOptions}
          value={selectedPeriode}
          onChange={setSelectedPeriode}
          className="w-full sm:w-56"
        />
      </div>

      {/* Alerts */}
      {kpiData.overdue > 0 && (
        <div className="p-3 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-danger shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-danger">
              {kpiData.overdue} laporan sudah melewati deadline!
            </p>
          </div>
          <Link
            to="/laporan?status=overdue"
            className="text-xs font-semibold text-danger hover:underline shrink-0"
          >
            Lihat →
          </Link>
        </div>
      )}

      {kpiData.menunggu_validasi > 0 && (
        <div className="p-3 bg-warning/5 border border-warning/20 rounded-xl flex items-center gap-3">
          <CheckSquare className="w-5 h-5 text-warning shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-warning">
              {kpiData.menunggu_validasi} laporan menunggu validasi Anda
            </p>
          </div>
          <Link
            to="/validasi/level1"
            className="text-xs font-semibold text-warning hover:underline shrink-0"
          >
            Validasi Sekarang →
          </Link>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          {
            title: 'Total Laporan',
            value: kpiData.total_laporan,
            icon: <FileText className="w-5 h-5" />,
            color: 'text-teal-main',
            bg: 'bg-teal-main/10',
          },
          {
            title: 'Menunggu Validasi',
            value: kpiData.menunggu_validasi,
            icon: <CheckSquare className="w-5 h-5" />,
            color: 'text-warning',
            bg: 'bg-warning/10',
            link: '/validasi/level1',
          },
          {
            title: 'Dalam Proses',
            value: kpiData.dalam_proses,
            icon: <Clock className="w-5 h-5" />,
            color: 'text-info',
            bg: 'bg-info/10',
          },
          {
            title: 'Konfirmasi',
            value: kpiData.menunggu_konfirmasi,
            icon: <Hourglass className="w-5 h-5" />,
            color: 'text-purple-600',
            bg: 'bg-purple-100',
          },
          {
            title: 'Selesai',
            value: kpiData.selesai,
            icon: <TrendingUp className="w-5 h-5" />,
            color: 'text-success',
            bg: 'bg-success/10',
          },
          {
            title: 'Terlambat',
            value: kpiData.overdue,
            icon: <AlertTriangle className="w-5 h-5" />,
            color: 'text-danger',
            bg: 'bg-danger/10',
          },
        ].map((kpi, i) =>
          kpi.link ? (
            <Link key={i} to={kpi.link}>
              <Card hover className="h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                      {kpi.title}
                    </p>
                    <p className={`text-2xl font-black mt-1 ${kpi.color}`}>
                      {kpi.value}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${kpi.bg} ${kpi.color}`}>
                    {kpi.icon}
                  </div>
                </div>
              </Card>
            </Link>
          ) : (
            <Card key={i}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                    {kpi.title}
                  </p>
                  <p className={`text-2xl font-black mt-1 ${kpi.color}`}>
                    {kpi.value}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${kpi.bg} ${kpi.color}`}>
                  {kpi.icon}
                </div>
              </div>
            </Card>
          )
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Validasi L1 */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-warning" />
              Menunggu Validasi Anda
            </h3>
            <Link
              to="/validasi/level1"
              className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1"
            >
              Lihat Semua <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {pendingValidasi.length > 0 ? (
              pendingValidasi.map((lap, i) => (
                <div
                  key={lap.id || i}
                  className="flex items-center gap-3 p-3 rounded-xl border border-neutral-border hover:bg-neutral-bg/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-teal-main">
                        {lap.kode}
                      </span>
                      <Badge
                        variant="risk"
                        risk={(lap.tingkat_risiko || lap.risiko || 'sedang') as TingkatRisiko}
                      />
                    </div>
                    <p className="text-sm font-medium truncate">{lap.judul}</p>
                    <p className="text-xs text-neutral-text-secondary">
                      {lap.unit?.name || '-'} •{' '}
                      {lap.user?.name || '-'}
                    </p>
                  </div>
                  {/* ✅ FIX: Gunakan route yang benar untuk validasi */}
                  <Link to={`/laporan/${lap.id}`} state={{ fromValidation: true }}>
                    <button className="px-3 py-1.5 text-xs font-semibold text-white bg-warning rounded-lg hover:bg-warning/90 transition-colors">
                      Review
                    </button>
                  </Link>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckSquare className="w-10 h-10 mx-auto mb-2 text-success opacity-50" />
                <p className="text-sm text-neutral-text-secondary">
                  Tidak ada laporan menunggu validasi
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Risiko Distribution */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4">Distribusi Risiko</h3>
          <div className="space-y-3">
            {risikoData.map((item, i) => {
              const percentage =
                kpiData.total_laporan > 0
                  ? Math.round((item.jumlah / kpiData.total_laporan) * 100)
                  : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span className="text-sm font-medium">{item.risiko}</span>
                    </div>
                    <span className="text-sm font-bold">
                      {item.jumlah}{' '}
                      <span className="text-xs text-neutral-text-secondary font-normal">
                        ({percentage}%)
                      </span>
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.fill,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Tindak Lanjut di Fakultas - ✅ FIXED */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Tindak Lanjut di {isKalem ? 'Lembaga' : 'Fakultas'}
          </h3>
          <Link
            to="/laporan?scope=pic"
            className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1"
          >
            Lihat Semua <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="space-y-3">
          {tindakLanjut.length > 0 ? (
            tindakLanjut.map((item, i) => {
              // ✅ FIX: Gunakan helper function yang sudah diperbaiki
              const days = getDaysRemaining(item);
              const isOverdue = days !== null && days < 0;
              const isUrgent = days !== null && days >= 0 && days <= 7;
              const statusInfo = getTindakLanjutStatusLabel(item);
              const isCompleted = isCompletedOrPending(item);

              const judul =
                item.laporan_masalah?.judul ||
                item.laporanMasalah?.judul ||
                '-';
              const kode =
                item.laporan_masalah?.kode ||
                item.laporanMasalah?.kode ||
                '-';

              return (
                <div
                  key={item.id || i}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${isCompleted
                    ? 'border-success/20 bg-success/5'
                    : isOverdue
                      ? 'border-danger/30 bg-danger/5'
                      : isUrgent
                        ? 'border-warning/30 bg-warning/5'
                        : 'border-neutral-border hover:bg-neutral-bg/50'
                    }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs font-mono text-teal-main">
                        {kode}
                      </span>
                      {/* ✅ FIX: Status badge yang sudah diperbaiki */}
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                      {/* ✅ FIX: Hanya tampilkan sisa hari jika BELUM selesai */}
                      {days !== null && (
                        <span
                          className={`text-[10px] font-semibold ${isOverdue
                            ? 'text-danger'
                            : isUrgent
                              ? 'text-warning'
                              : 'text-neutral-text-secondary'
                            }`}
                        >
                          {isOverdue
                            ? `${Math.abs(days)}d late`
                            : `${days}d left`}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium truncate">{judul}</p>
                    <p className="text-xs text-neutral-text-secondary">
                      PIC: {item.pic_user?.name || '-'}
                    </p>
                  </div>
                  <Link to={`/tindak-lanjut/${item.id}`}>
                    <button className="p-2 rounded-lg hover:bg-teal-main/10 text-neutral-text-secondary hover:text-teal-main transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              );
            })
          ) : (
            <div className="text-center py-6">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-neutral-text-secondary opacity-40" />
              <p className="text-sm text-neutral-text-secondary">
                Belum ada tindak lanjut aktif
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Trend */}
      <Card>
        <h3 className="text-base font-bold text-teal-dark mb-4">
          Tren Laporan 6 Bulan Terakhir
        </h3>
        {trendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="bulan" tick={{ fontSize: 12, fill: '#6B7280' }} />
              <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '12px',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="jumlah"
                stroke="#0f33ab"
                strokeWidth={2.5}
                name="Laporan Masuk"
                dot={{ fill: '#0f33ab', r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="selesai"
                stroke="#22C55E"
                strokeWidth={2.5}
                name="Selesai"
                dot={{ fill: '#22C55E', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[250px] flex items-center justify-center text-neutral-text-secondary">
            Belum ada data tren
          </div>
        )}
      </Card>
    </div>
  );
};

export default DashboardDekan;
